import { Fragment, useState } from "react";
import { GameWordmark } from "../components/GameWordmark.js";
import { Spark } from "../components/Spark.js";
import { gradeFor, toneColor, toneSoft } from "../data/game.js";
import { PLAN_LIMITS, siteStats } from "../store/types.js";
import { store, useBrutallyStore } from "../store/sites.js";
import { SiteHistory } from "./SiteHistory.js";

interface Props {
  onPlay: (url?: string) => void;
  onHome: () => void;
  onUpgrade: () => void;
  authEmail: string | null;
}

export function DashboardGame({ onPlay, onHome, onUpgrade, authEmail }: Props) {
  const state = useBrutallyStore();
  const planKey = state.plan;
  const planCfg = PLAN_LIMITS[planKey];
  const [activeId, setActiveId] = useState<string | null>(state.sites[0]?.id ?? null);

  // Keep activeId in sync if sites change
  if (activeId && !state.sites.find((s) => s.id === activeId)) {
    setActiveId(state.sites[0]?.id ?? null);
  } else if (!activeId && state.sites[0]) {
    setActiveId(state.sites[0].id);
  }

  const visibleSites = state.sites;
  const lockedSlots = planCfg.unlimited ? 0 : Math.max(0, planCfg.siteSlots - visibleSites.length) + 1;
  const active = state.sites.find((s) => s.id === activeId) ?? null;

  const totalPlays = visibleSites.reduce((n, s) => n + s.runs.length, 0);
  const biggestClimb = visibleSites.length ? Math.max(0, ...visibleSites.map((s) => siteStats(s).total)) : 0;
  const coinsLabelN = state.coins.left;

  const onTogglePlan = () => store.setPlan(planKey === "pro" ? "free" : "pro");
  const onShare = () => {
    if (navigator.clipboard) navigator.clipboard.writeText(window.location.href);
    store.markShare();
  };

  return (
    <div className="gdash screen-enter">
      <header className="gdash-bar">
        <div className="wrap gdash-bar-inner">
          <div className="gdash-bar-left">
            <GameWordmark size={19} onClick={onHome} />
            <span className="glabel gdash-tag">Player cabinet</span>
          </div>
          <div className="gdash-bar-right">
            {planKey === "free" ? (
              <span className="coinchip">
                <span className="coinchip-coins">
                  {Array.from({ length: state.coins.max }).map((_, i) => (
                    <span key={i} className={`coin ${i < state.coins.left ? "is-full" : "is-empty"}`} />
                  ))}
                </span>
                <span className="coinchip-label">{coinsLabelN} {coinsLabelN === 1 ? "play" : "plays"}</span>
              </span>
            ) : (
              <span className="coinchip">
                <span className="coin is-full">∞</span>
                <span className="coinchip-label">Unlimited</span>
              </span>
            )}
            {authEmail ? (
              <span
                className={`plan-badge is-pro`}
                title={`Signed in as ${authEmail}`}
                style={{ cursor: "default" }}
              >
                PRO
              </span>
            ) : (
              <button
                className={`plan-badge ${planKey === "pro" ? "is-pro" : "is-free"}`}
                onClick={onTogglePlan}
                title="Toggle plan (demo)"
              >
                {planKey === "pro" ? "PRO" : "FREE"}
              </button>
            )}
            <span className="gdash-avatar" title={authEmail ?? ""}>
              {authEmail ? authEmail[0].toUpperCase() : "B"}
            </span>
          </div>
        </div>
      </header>

      <div className="wrap gdash-inner">
        <div className="cabinet">
          <div className="gcard cabinet-main">
            <div className="cabinet-hello">
              <span className="cabinet-hello-av">
                {authEmail ? authEmail[0].toUpperCase() : "B"}
              </span>
              <div>
                <h1 className="gdisplay cabinet-name">Welcome back.</h1>
                <p className="glabel cabinet-meta">
                  {authEmail ? <>signed in as <b>{authEmail}</b> · </> : null}
                  rank: {visibleSites.length === 0 ? "Rookie" : biggestClimb > 20 ? "Veteran" : "Apprentice"}
                </p>
              </div>
            </div>
            <div className="cabinet-stats">
              <div className="cabinet-stat">
                <div className="gdisplay cabinet-stat-num">
                  {visibleSites.length}
                  <span style={{ fontSize: "0.4em", color: "var(--ink-faint)" }}>
                    {planCfg.unlimited ? "" : `/${planCfg.siteSlots}`}
                  </span>
                </div>
                <div className="glabel cabinet-stat-label">Sites tracked</div>
              </div>
              <div className="cabinet-stat">
                <div className="gdisplay cabinet-stat-num">{totalPlays}</div>
                <div className="glabel cabinet-stat-label">Total plays</div>
              </div>
              <div className="cabinet-stat">
                <div className="gdisplay cabinet-stat-num" style={{ color: "var(--green)" }}>+{biggestClimb}</div>
                <div className="glabel cabinet-stat-label">Biggest climb</div>
              </div>
            </div>
          </div>

          <div className={`gcard plays ${planKey === "pro" ? "is-pro" : ""}`}>
            {planKey === "free" ? (
              <Fragment>
                <div className="plays-head">
                  <span className="glabel">Free plays</span>
                  <span className="pill pill--yellow" style={{ fontSize: 10 }}>Insert coin</span>
                </div>
                <div className="plays-coins">
                  {Array.from({ length: state.coins.max }).map((_, i) => (
                    <span key={i} className={`plays-coin ${i < state.coins.left ? "is-full" : "is-empty"}`}>
                      {i < state.coins.left ? "★" : ""}
                    </span>
                  ))}
                </div>
                <div className="gdisplay plays-big">
                  {state.coins.left}
                  <small> / {state.coins.max} left</small>
                </div>
                <div className="plays-earn">
                  <div className={`plays-earn-row ${state.coins.earned.firstSave ? "is-done" : ""}`}>
                    <span className={`plays-earn-check ${state.coins.earned.firstSave ? "is-done" : ""}`}>
                      {state.coins.earned.firstSave ? "✓" : "+1"}
                    </span>
                    Save your first site
                  </div>
                  <div className={`plays-earn-row ${state.coins.earned.share ? "is-done" : ""}`}>
                    <span className={`plays-earn-check ${state.coins.earned.share ? "is-done" : ""}`}>
                      {state.coins.earned.share ? "✓" : "+1"}
                    </span>
                    Share a verdict
                  </div>
                  <div className="plays-earn-row">
                    <span className="plays-earn-check">+1</span>
                    Ship 3 fixes, re-play
                  </div>
                </div>
                <button className="gbtn gbtn--primary gbtn--block" onClick={onUpgrade}>Go unlimited with Pro →</button>
              </Fragment>
            ) : (
              <Fragment>
                <div className="plays-head">
                  <span className="glabel">Plays</span>
                  <span className="pill" style={{ fontSize: 10, background: "var(--yellow)" }}>PRO</span>
                </div>
                <div className="gdisplay plays-big" style={{ color: "var(--yellow)" }}>
                  ∞<small style={{ color: "#cfc9bb" }}> unlimited</small>
                </div>
                <div className="plays-pro-perk"><span className="upsell-li-ic">↻</span> Re-play any site, anytime.</div>
                <div className="plays-pro-perk"><span className="upsell-li-ic">🔔</span> Auto re-scan weekly. <b>&nbsp;Alerts on drops.</b></div>
                <div className="plays-pro-perk"><span className="upsell-li-ic">∎</span> Deep lens history on every site.</div>
              </Fragment>
            )}
          </div>
        </div>

        <div className="gdash-section-head">
          <h2 className="gdisplay gsection-title">Your machines.</h2>
          <span className="pill">
            {planKey === "free" ? `Free tracks ${planCfg.siteSlots} site` : `Pro · up to ${planCfg.siteSlots} sites`}
          </span>
        </div>

        {visibleSites.length === 0 ? (
          <div className="gcard slot-locked" style={{ cursor: "default" }}>
            <span className="slot-lock-ic">＋</span>
            <h4 className="gdisplay">No machines yet.</h4>
            <p className="soft">Run a site, then hit "＋ Save &amp; track" on the report to see it here.</p>
            <button className="gbtn gbtn--primary" onClick={() => onPlay()}>Run your first site</button>
          </div>
        ) : (
          <div className="sites-grid">
            {visibleSites.map((s) => {
              const st = siteStats(s);
              const g = gradeFor(st.current);
              const color = toneColor(s.tone);
              const isActive = s.id === activeId;
              const last = s.runs[s.runs.length - 1].date;
              return (
                <article
                  key={s.id}
                  className={`gcard sitecard ${isActive ? "is-active" : ""}`}
                  onClick={() => setActiveId(s.id)}
                >
                  <div className="sitecard-top">
                    <div className="sitecard-id">
                      <span className="sitecard-fav" style={{ background: toneSoft(s.tone) }}>{s.name[0]}</span>
                      <div style={{ minWidth: 0 }}>
                        <div className="sitecard-url">{s.url}</div>
                        <div className="glabel sitecard-last">Last played {last}</div>
                      </div>
                    </div>
                    <span className={`delta ${st.delta > 0 ? "is-up" : st.delta < 0 ? "is-down" : "is-flat"}`}>
                      {st.delta > 0 ? "▲ +" : st.delta < 0 ? "▼ " : "■ "}
                      {st.delta !== 0 ? Math.abs(st.delta) : "0"}
                    </span>
                  </div>
                  <div className="sitecard-mid">
                    <div className="sitecard-score">
                      <span className="gdisplay sitecard-num" style={{ color }}>
                        {st.current}<small>/100</small>
                      </span>
                      <span className="sitecard-grade" style={{ background: g.bg, color: g.fg }}>{g.letter}</span>
                    </div>
                    <div className="sitecard-spark">
                      <Spark runs={s.runs} color={color} />
                      <span className="glabel" style={{ fontSize: 10 }}>{st.plays} plays</span>
                    </div>
                  </div>
                  <div className="sitecard-foot">
                    <span className="glabel sitecard-plays">{s.questsDone}/{s.questsTotal} fixes shipped</span>
                    <span className="sitecard-cta" style={{ color: isActive ? "var(--red)" : "var(--ink)" }}>
                      {isActive ? "Viewing ↓" : "View history →"}
                    </span>
                  </div>
                </article>
              );
            })}

            {Array.from({ length: lockedSlots }).map((_, i) => (
              <button
                key={i}
                className="slot-locked"
                onClick={planKey === "free" ? onUpgrade : () => onPlay()}
              >
                <span className="slot-lock-ic">＋</span>
                <h4 className="gdisplay">
                  {planKey === "free" ? "Add another machine" : "Add a site"}
                </h4>
                <p className="soft">
                  {planKey === "free"
                    ? "Free tracks one site. Pro tracks up to eight, each with full history."
                    : "Run a site and save it from the report."}
                </p>
                {planKey === "free" && (
                  <span className="pill pill--yellow" style={{ fontSize: 10 }}>🔒 Unlock with Pro</span>
                )}
              </button>
            ))}
          </div>
        )}

        {active && (
          <SiteHistory
            site={active}
            plan={planKey}
            onReplay={() => onPlay(active.url)}
            onUpgrade={onUpgrade}
            onShare={onShare}
          />
        )}

        {planKey === "free" && (
          <div className="gcard upsell">
            <div>
              <span className="glabel">Stop playing on one credit</span>
              <h3 className="gdisplay upsell-h">Pro is the whole arcade.</h3>
              <p className="upsell-sub">
                Track every site you ship. Re-play as much as you want. Watch each lens move and get pinged the moment a score slips.
              </p>
              <div className="upsell-actions">
                <button className="gbtn gbtn--primary gbtn--lg" onClick={onUpgrade}>
                  Get Pro <span className="upsell-price" style={{ color: "#fff", marginLeft: 6 }}>$19<small>/mo</small></span>
                </button>
                <button
                  className="gbtn gbtn--lg"
                  onClick={() => onPlay()}
                  style={{ background: "transparent", color: "var(--paper)", borderColor: "var(--paper)" }}
                >
                  Keep playing free
                </button>
              </div>
            </div>
            <div className="upsell-list">
              <div className="upsell-li"><span className="upsell-li-ic">∞</span> Unlimited plays and re-runs</div>
              <div className="upsell-li"><span className="upsell-li-ic">8</span> Up to 8 tracked sites</div>
              <div className="upsell-li"><span className="upsell-li-ic">∎</span> Deep lens-by-lens history</div>
              <div className="upsell-li"><span className="upsell-li-ic">🔔</span> Weekly auto re-scans and drop alerts</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
